/**
 * legal-imprint — renders the § 5 DDG imprint block assembled from
 * `Workspace.legal`, inside whatever overlay mounts it, or embedded
 * directly on the public /legal page — the same dual-mount shape
 * legal-document.view.ts uses for terms/privacy, including its own
 * `dismissible` property and close-button contract, reused rather than
 * reinvented. Content-only per schmancy's overlay contract: no full-bleed
 * surface, no padding from the host — this component supplies its own.
 *
 * Never mounted for a null/incomplete `legal` block — every caller already
 * gates on `imprintAvailable` (legal/legal.state.ts) before
 * rendering the link that opens this; a withheld statutory disclosure
 * beats a fabricated one, so this renders nothing rather than guessing at
 * placeholders when `legal` is absent regardless.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import type { Workspace } from "@momo/types";
import { html } from "lit";
import { property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../i18n/localize.ts";

@schmancyElement("legal-imprint")
export class LegalImprintView extends LocalizeMixin(SchmancyElement) {
	@property({ attribute: false }) legal: NonNullable<
		Workspace.Workspace["legal"]
	> | null = null;
	/** Whether this document offers its own close affordance — mirrors
	 *  legal-document.view.ts's own `dismissible`: true by default for an
	 *  overlay mount, set false by legal-page.view.ts, which embeds this
	 *  directly with nothing to close. */
	@property({ type: Boolean }) dismissible = true;

	override render() {
		const legal = this.legal;
		if (!legal) return html``;
		return html`
			<div class="flex flex-col gap-4 w-full max-w-2xl p-6">
				<div class="flex items-start justify-between gap-4">
					<schmancy-typography type="headline" token="sm">
						${this.t("legal.imprint.heading")}
					</schmancy-typography>
					${when(
						this.dismissible,
						() => html`
							<schmancy-icon-button
								icon="close"
								variant="text"
								size="sm"
								aria-label=${this.t("legal.viewer.close")}
								@click=${() =>
									this.dispatchEvent(
										new CustomEvent("close", {
											bubbles: true,
											composed: true,
										}),
									)}
							></schmancy-icon-button>
						`,
					)}
				</div>
				<dl class="grid grid-cols-[auto_1fr] items-baseline gap-x-4 gap-y-2">
					<dt>
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
							${this.t("legal.imprint.legalName")}
						</schmancy-typography>
					</dt>
					<dd>
						<schmancy-typography type="body" token="sm">${legal.legalName}</schmancy-typography>
					</dd>
					<dt>
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
							${this.t("legal.imprint.address")}
						</schmancy-typography>
					</dt>
					<dd>
						<schmancy-typography type="body" token="sm">${legal.address}</schmancy-typography>
					</dd>
					<dt>
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
							${this.t("legal.imprint.representative")}
						</schmancy-typography>
					</dt>
					<dd>
						<schmancy-typography type="body" token="sm">${legal.representative}</schmancy-typography>
					</dd>
					<dt>
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
							${this.t("legal.imprint.register")}
						</schmancy-typography>
					</dt>
					<dd>
						<schmancy-typography type="body" token="sm">${legal.register}</schmancy-typography>
					</dd>
					<dt>
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
							${this.t("legal.imprint.vatId")}
						</schmancy-typography>
					</dt>
					<dd>
						<schmancy-typography type="body" token="sm">${legal.vatId}</schmancy-typography>
					</dd>
					<dt>
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
							${this.t("legal.imprint.contactEmail")}
						</schmancy-typography>
					</dt>
					<dd>
						<schmancy-typography type="body" token="sm">${legal.contactEmail}</schmancy-typography>
					</dd>
					${when(
						!!legal.phone,
						() => html`
							<dt>
								<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
									${this.t("legal.imprint.phone")}
								</schmancy-typography>
							</dt>
							<dd>
								<schmancy-typography type="body" token="sm">${legal.phone}</schmancy-typography>
							</dd>
						`,
					)}
				</dl>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"legal-imprint": LegalImprintView;
	}
}
